'use client';

import { X } from 'lucide-react';
import type { MetricDefinition } from '../lib/metric-definitions';
import type { MetricData } from '../hooks/use-metrics-data';
import { fmtMoney, fmtCount } from '../lib/format';

interface Props {
  def: MetricDefinition;
  data?: MetricData;
  isActive: boolean;
  /** Click pe card → deschide modal cu detalii pentru acest metric */
  onShowDetail: () => void;
  /** Click pe X (hover) → scoate metricul din lista activă */
  onRemove: () => void;
  /** Disable removal (e.g. when only 1 metric left) */
  canRemove?: boolean;
  isLoading?: boolean;
}

export function MetricTile({ def, data, isActive, onShowDetail, onRemove, canRemove = true, isLoading }: Props) {
  const total = data?.current || 0;
  const delta = data?.deltaPct || 0;
  const series = data?.series || [];
  const displayVal = def.unit === 'money' ? fmtMoney(total) : fmtCount(total);
  const unit = def.unit === 'money' ? 'RON' : (def.id === 'newClients' ? 'clienți' : 'facturi');

  const deltaClass = Math.abs(delta) < 1 ? 'flat' : (delta > 0 ? 'up' : 'down');
  const deltaText = Math.abs(delta) < 1 ? 'stabil' : ((delta > 0 ? '+' : '') + delta.toFixed(1) + '%');

  // Sparkline
  // Inset vertical: linia nu mai atinge marginile (y=0 / y=100) ale viewBox-ului,
  // ca să nu se taie conturul de 1px la marginea de jos (clipping dependent de zoom).
  const SPARK_PAD = 12;
  const sparkMax = series.length > 0 ? Math.max(...series.map((s) => s.v)) : 1;
  const sparkMin = series.length > 0 ? Math.min(...series.map((s) => s.v)) : 0;
  const sparkRange = sparkMax - sparkMin || 1;
  const sparkPath = series.length > 1
    ? series.map((p, i) => {
        const x = (i / (series.length - 1)) * 100;
        const y = SPARK_PAD + (1 - (p.v - sparkMin) / sparkRange) * (100 - 2 * SPARK_PAD);
        return (i === 0 ? 'M' : 'L') + x + ',' + y;
      }).join(' ')
    : '';

  return (
    <div
      className={
        'sf-metric-tile group relative bg-transparent transition-colors border-r border-neutral-100 dark:border-neutral-800 last:border-r-0 ' +
        (isLoading ? 'opacity-50' : '') +
        (isActive ? ' sf-metric-tile-active' : ' hover:bg-neutral-50 dark:hover:bg-neutral-900/60')
      }
      style={{ ['--tile-color' as any]: def.color }}
    >
      {/* Remove button — vizibil la hover, dezactivat dacă e ultimul card */}
      {canRemove && (
        <button
          type="button"
          onClick={onRemove}
          aria-label={`Ascunde ${def.label}`}
          title={`Ascunde ${def.label}`}
          className="absolute top-1.5 right-1.5 z-10 opacity-0 group-hover:opacity-100 transition-opacity grid place-items-center w-5 h-5 rounded-full bg-white/80 dark:bg-neutral-900/80 text-neutral-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 border border-neutral-200 dark:border-neutral-700 cursor-pointer"
        >
          <X className="h-3 w-3" />
        </button>
      )}

      <button
        type="button"
        onClick={onShowDetail}
        disabled={isLoading}
        className="block w-full text-left bg-transparent border-0 cursor-pointer px-4 py-3.5 disabled:cursor-wait"
      >
      <div className="flex items-center gap-2 mb-2.5">
        <span
          className={
            'w-2 h-2 rounded-full transition-all shrink-0 ' +
            (isActive ? '' : 'border-2')
          }
          style={{
            background: isActive ? def.color : 'transparent',
            borderColor: isActive ? 'transparent' : '#9A9791',
            boxShadow: isActive ? '0 0 0 3px ' + def.color + '25' : 'none',
          }}
        />
        <span
          className={
            'text-[0.6875rem] font-semibold uppercase tracking-[0.06em] transition-colors ' +
            (isActive ? 'text-neutral-700 dark:text-neutral-200' : 'text-neutral-500 dark:text-neutral-400')
          }
        >
          {def.label}
        </span>
      </div>

      <div
        className={'transition-opacity ' + (isActive ? 'opacity-100' : 'opacity-40')}
      >
        <div
          className="font-medium leading-none tracking-[-0.02em] text-neutral-900 dark:text-neutral-100 mb-1.5 tabular-nums"
          style={{ fontFamily: '"Fraunces", Georgia, serif', fontSize: '1.375rem' }}
        >
          {displayVal}
          <span
            className="ml-1 text-[0.625rem] font-medium text-neutral-500 dark:text-neutral-400 align-super"
            style={{ fontFamily: '"Geist", sans-serif' }}
          >
            {unit}
          </span>
        </div>

        <div
          className={
            'inline-flex items-center gap-1 font-mono text-[0.6563rem] font-semibold ' +
            (deltaClass === 'up'
              ? 'text-emerald-700 dark:text-emerald-400'
              : deltaClass === 'down'
                ? 'text-red-700 dark:text-red-400'
                : 'text-neutral-400')
          }
        >
          {deltaClass === 'flat' && (
            <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><line x1="5" y1="12" x2="19" y2="12" /></svg>
          )}
          {deltaClass === 'up' && (
            <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="18 15 12 9 6 15" /></svg>
          )}
          {deltaClass === 'down' && (
            <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="6 9 12 15 18 9" /></svg>
          )}
          {deltaText}
        </div>
      </div>

      {sparkPath && (
        <svg
          className="mt-2 w-full"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          style={{ height: 22, overflow: 'visible' }}
        >
          <path
            d={sparkPath}
            fill="none"
            stroke={def.color}
            strokeWidth="1"
            strokeLinecap="butt"
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
            opacity={isActive ? 0.9 : 0.35}
          />
        </svg>
      )}

      </button>

      {isActive && (
        <div
          className="absolute left-0 right-0 bottom-0 h-[2px] pointer-events-none"
          style={{ background: def.color }}
        />
      )}
    </div>
  );
}
